/**
 * ReimbursementFeesTab
 * Read-only table with filter bar (distributor, operator, location, date range + Search)
 */

import { useState, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  SortingState,
  ColumnDef,
} from '@tanstack/react-table';
import { Loader2, Search, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { Input } from '@/core/components/ui/input';
import { Label } from '@/core/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/core/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/core/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/core/components/ui/command';
import { GRID_STYLES } from '@/core/components/ui/grid-styles';
import { cn } from '@/core/lib/utils';
import { useBillingStore } from '../../stores/billing-store';
import { BillingApi } from '../../data/billing-api';
import { BASE_AUTH_OFFICE_API } from '@/core/api-config';
import { servicesContainer } from '@/core/di/services-container';
import type { ReimbursementFee, ReimbursementFeesFilters } from '../../types/billing-types';

export function ReimbursementFeesTab() {
  const {
    reimbursementFees,
    isLoadingReimbursementFees,
    reimbursementFeesError,
    loadReimbursementFees,
  } = useBillingStore();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [filters, setFilters] = useState<ReimbursementFeesFilters>({
    distributorId: '',
    operatorId: '',
    actorId: '',
    startDate: '',
    endDate: '',
  });
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const pageSizeOptions = [10, 25, 50, 100];

  // Distributor dropdown state
  const [distributors, setDistributors] = useState<Array<{ id: number; name: string }>>([]);
  const [distributorsLoading, setDistributorsLoading] = useState(false);
  const [distributorOpen, setDistributorOpen] = useState(false);

  useEffect(() => {
    setDistributorsLoading(true);
    const services = servicesContainer.getServices();
    const api = new BillingApi(BASE_AUTH_OFFICE_API, services.tokenManager);
    api.getDistributors()
      .then(setDistributors)
      .finally(() => setDistributorsLoading(false));
  }, []);

  const handleSearch = () => {
    setHasSearched(true);
    setCurrentPage(1);
    loadReimbursementFees(filters);
  };

  const formatDate = (v: string | null) => (v ? new Date(v).toLocaleDateString() : '—');

  const selectedDistributor = distributors.find(d => d.id.toString() === filters.distributorId);

  const displayedFees = reimbursementFees;

  const columns: ColumnDef<ReimbursementFee>[] = [
    { accessorKey: 'id', header: 'ID', size: 70 },
    {
      accessorKey: 'invoiceStartDate',
      header: 'Invoice Start',
      cell: ({ getValue }) => formatDate(getValue() as string | null),
      size: 120,
    },
    {
      accessorKey: 'invoiceEndDate',
      header: 'Invoice End',
      cell: ({ getValue }) => formatDate(getValue() as string | null),
      size: 120,
    },
    {
      accessorKey: 'distributorId',
      header: 'Distributor ID',
      cell: ({ getValue }) => (getValue() as number | null) ?? '—',
      size: 120,
    },
    {
      accessorKey: 'actorId',
      header: 'Actor ID',
      cell: ({ getValue }) => (getValue() as number | null) ?? '—',
      size: 100,
    },
    {
      accessorKey: 'machineId',
      header: 'Machine ID',
      cell: ({ getValue }) => (getValue() as number | null) ?? '—',
      size: 110,
    },
    {
      accessorKey: 'feeTypeId',
      header: 'Fee Type',
      cell: ({ getValue }) => (getValue() as number | null) ?? '—',
      size: 90,
    },
    {
      accessorKey: 'count',
      header: 'Count',
      cell: ({ getValue }) => (getValue() as number | null) ?? '—',
      size: 80,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (getValue() as number | null) ?? '—',
      size: 80,
    },
    {
      accessorKey: 'sentDt',
      header: 'Sent',
      cell: ({ getValue }) => {
        const v = getValue() as string | null;
        return v ? new Date(v).toLocaleDateString() : '';
      },
      size: 110,
    },
    {
      accessorKey: 'createdDt',
      header: 'Created',
      cell: ({ getValue }) => formatDate(getValue() as string | null),
      size: 110,
    },
  ];

  const table = useReactTable({
    data: displayedFees,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: { sorting },
  });

  const totalCount = displayedFees.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);
  const pagedRows = table.getRowModel().rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const getPageNumbers = () => {
    const pages: number[] = [];
    const maxPagesToShow = 4;
    let startPage = Math.max(1, currentPage - 1);
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    startPage = endPage - maxPagesToShow + 1 < 1 ? 1 : endPage - maxPagesToShow + 1;
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  };

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {/* Distributor dropdown */}
          <div className="space-y-1.5">
            <Label>Distributor</Label>
            <Popover open={distributorOpen} onOpenChange={setDistributorOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={distributorOpen}
                  className="w-full justify-between font-normal h-10"
                  disabled={distributorsLoading}
                >
                  {distributorsLoading ? (
                    <span className="flex items-center gap-2 text-gray-400">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading...
                    </span>
                  ) : selectedDistributor ? (
                    <span className="truncate">{selectedDistributor.name}</span>
                  ) : (
                    <span className="text-gray-400">Any</span>
                  )}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search distributor..." />
                  <CommandList
                    className="max-h-60 overflow-y-auto"
                    onWheel={(e) => e.stopPropagation()}
                  >
                    <CommandEmpty>No distributors found</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="__all__"
                        onSelect={() => {
                          setFilters({ ...filters, distributorId: '' });
                          setDistributorOpen(false);
                        }}
                      >
                        <Check
                          className={cn('mr-2 h-4 w-4', !filters.distributorId ? 'opacity-100' : 'opacity-0')}
                        />
                        <span className="text-gray-500">Any distributor</span>
                      </CommandItem>
                      {distributors.map((d) => (
                        <CommandItem
                          key={d.id}
                          value={`${d.name} ${d.id}`}
                          onSelect={() => {
                            setFilters({ ...filters, distributorId: d.id.toString() });
                            setDistributorOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              filters.distributorId === d.id.toString() ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                          <div className="flex flex-col">
                            <span className="text-sm">{d.name}</span>
                            <span className="text-xs text-gray-500">ID: {d.id}</span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Start Date */}
          <div className="space-y-1.5">
            <Label htmlFor="startDate">Start Date</Label>
            <Input
              id="startDate"
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            />
          </div>

          {/* End Date */}
          <div className="space-y-1.5">
            <Label htmlFor="endDate">End Date</Label>
            <Input
              id="endDate"
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button
            onClick={handleSearch}
            disabled={isLoadingReimbursementFees}
            className="flex items-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-semibold"
          >
            {isLoadingReimbursementFees ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            Search
          </Button>
        </div>
      </div>

      {/* Error */}
      {reimbursementFeesError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {reimbursementFeesError}
        </div>
      )}

      {/* Table */}
      <div className={`rounded-md border ${GRID_STYLES.border} overflow-hidden`}>
        {isLoadingReimbursementFees ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : !hasSearched ? (
          <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
            Use the filters above and click Search to load data
          </div>
        ) : displayedFees.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-gray-500 text-sm">
            No reimbursement fees found for the selected filters
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={cn(GRID_STYLES.header.background, 'sticky top-0 z-10')}>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'cursor-pointer select-none whitespace-nowrap')}
                        style={{ width: header.getSize() }}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div className="flex items-center gap-1">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <div className="flex flex-col">
                              <ChevronUp className={`h-3 w-3 ${header.column.getIsSorted() === 'asc' ? GRID_STYLES.sort.active : GRID_STYLES.sort.inactive}`} />
                              <ChevronDown className={`h-3 w-3 -mt-1 ${header.column.getIsSorted() === 'desc' ? GRID_STYLES.sort.active : GRID_STYLES.sort.inactive}`} />
                            </div>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {pagedRows.map((row) => (
                  <tr key={row.id} className={cn(GRID_STYLES.row.default, GRID_STYLES.row.hover, `border-b ${GRID_STYLES.border}`, 'transition-colors')}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className={cn(GRID_STYLES.cell.base, 'whitespace-nowrap')}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {hasSearched && !isLoadingReimbursementFees && totalCount > 0 && (
        <div className="flex items-center justify-between px-2 py-2 border border-[#E8E8E8] rounded-b-md bg-white -mt-3 border-t-0">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="h-8 w-8 p-0 border-[#E8E8E8]">
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="h-8 w-8 p-0 border-[#E8E8E8]">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {getPageNumbers().map(pageNum => (
              <Button
                key={pageNum}
                variant={currentPage === pageNum ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCurrentPage(pageNum)}
                className={`h-8 w-8 p-0 ${currentPage === pageNum ? 'bg-[#FFCD00] hover:bg-[#E6B800] text-[#262626] border-[#FFCD00]' : 'border-[#E8E8E8]'}`}
              >
                {pageNum}
              </Button>
            ))}
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="h-8 w-8 p-0 border-[#E8E8E8]">
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="h-8 w-8 p-0 border-[#E8E8E8]">
              <ChevronsRight className="h-4 w-4" />
            </Button>
            <span className="text-sm text-[#606060] ml-2">{currentPage} of {totalPages} pages</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#606060]">{startItem}-{endItem} of {totalCount.toLocaleString()}</span>
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#606060]">Rows per page:</span>
              <Select value={pageSize.toString()} onValueChange={v => { setPageSize(parseInt(v, 10)); setCurrentPage(1); }}>
                <SelectTrigger className="h-8 w-[70px] border-[#E8E8E8] text-[#262626]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pageSizeOptions.map(size => (
                    <SelectItem key={size} value={size.toString()} className="hover:bg-[#FFFBEB]">{size}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
